import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ClassifyPage } from "../api/client";
import styles from "./ClassifyDetailPanel.module.css";

/**
 * A single sortable filmstrip thumbnail.
 * Defined as its own component so the useSortable hook is isolated per item.
 * Extracted from ClassifyDetailPanel.tsx (R-260 CS-station cleanup).
 */
export function SortableThumb({
  page,
  index,
  count,
  selected,
  onSelect,
  onMove,
  disabled,
}: {
  page: ClassifyPage;
  index: number;
  count: number;
  selected: boolean;
  onSelect: () => void;
  onMove: (dir: -1 | 1) => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: page.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={styles.filmstripItem}>
      <button
        type="button"
        className={styles.filmstripThumb}
        aria-label={`Thumbnail for page ${page.pageNumber}`}
        aria-current={selected ? "true" : undefined}
        onClick={onSelect}
        {...attributes}
        {...listeners}
      >
        {page.thumbnailPath ? (
          <img
            src={page.thumbnailPath}
            alt={`Thumbnail page ${page.pageNumber}`}
            className={styles.filmstripImg}
          />
        ) : (
          <div className={styles.filmstripImgPlaceholder} aria-hidden="true" />
        )}
        <span className={styles.filmstripBadge}>
          {page.pageTypeName ?? page.pageLabel ?? "unknown"}
        </span>
      </button>
      <div className={styles.filmstripMove}>
        <button
          type="button"
          aria-label={`Move page ${page.pageNumber} left`}
          disabled={disabled || index === 0}
          onClick={() => onMove(-1)}
        >
          ‹
        </button>
        <button
          type="button"
          aria-label={`Move page ${page.pageNumber} right`}
          disabled={disabled || index === count - 1}
          onClick={() => onMove(1)}
        >
          ›
        </button>
      </div>
    </div>
  );
}
